import React, { useEffect, useState, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Card } from '../components/SubscriptionAppCard';
import { Input } from '../components/SubscriptionAppInput';
import { useSubscriptionForm } from '../hooks/useSubscriptionForm';
import { mockEvents, availableProducts, availableTags, mockPaymentRetryHistory } from '../data/mockSubscriptionData';
import { CreditCard, Calendar, ShoppingCart, X, Package, AlertTriangle } from 'lucide-react';
import { TagInput } from '../components/TagInput';
import type { TagData } from '../components/TagInput';
// Modular Components
import { SubscriptionHeader } from '../components/subscription-detail/SubscriptionHeader';
import { ProductSelector } from '../components/subscription-detail/ProductSelector';
import { AddressForm } from '../components/subscription-detail/AddressForm';
import { CustomerSummary } from '../components/subscription-detail/CustomerSummary';
import { ActivityTimeline, Event } from '../components/subscription-detail/ActivityTimeline';
import { OrdersTable } from '../components/OrdersTable';
import { PaymentMethodModal, PaymentData } from '../components/subscription-detail/PaymentMethodModal';
import { DateTimePicker } from '../components/subscription-detail/DateTimePicker';
import { PaymentStatusBanner } from '../components/subscription-detail/PaymentStatusBanner';
import { PaymentRetryHistory } from '../components/subscription-detail/PaymentRetryHistory';
import { SendPaymentLinkModal } from '../components/subscription-detail/SendPaymentLinkModal';
import { CreateTaskModal } from '../components/CreateTaskModal';
import type { TaskData } from '../components/CreateTaskModal';
import { useActivities } from '../contexts/ActivityContext';
import { useTags } from '../contexts/TagsContext';
import { Badge } from '../components/SubscriptionAppBadge';
interface SubscriptionDetailPageProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}
export function SubscriptionDetailPage({
  currentPage = 'Subscriptions',
  onNavigate
}: SubscriptionDetailPageProps) {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  // Use onNavigate prop if provided, otherwise use local navigate
  const handleNavigate = onNavigate || ((page: string) => {
    if (page === 'Subscriptions') navigate('/subscriptions');else if (page === 'Orders') navigate('/orders');else navigate('/');
  });
  const [copyShippingToBilling, setCopyShippingToBilling] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isSendPaymentLinkModalOpen, setIsSendPaymentLinkModalOpen] = useState(false);
  const [isOrderNowModalOpen, setIsOrderNowModalOpen] = useState(false);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState({
    type: 'Visa',
    last4: '4242',
    expiry: '01/26'
  });
  // Customer contact state
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [initialCustomerEmail, setInitialCustomerEmail] = useState('');
  const [initialCustomerPhone, setInitialCustomerPhone] = useState('');
  // Helper function to check if card is expiring soon
  const isCardExpiringSoon = (expiry: string) => {
    const [month, year] = expiry.split('/');
    const expiryDate = new Date(2000 + parseInt(year), parseInt(month) - 1);
    const today = new Date();
    const sixtyDaysFromNow = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);
    return expiryDate <= sixtyDaysFromNow && expiryDate >= today;
  };
  const isCardExpired = (expiry: string) => {
    const [month, year] = expiry.split('/');
    const expiryDate = new Date(2000 + parseInt(year), parseInt(month) - 1);
    const today = new Date();
    return expiryDate < today;
  };
  const {
    loading,
    subscription,
    isEditing,
    setIsEditing,
    hasChanges: formHasChanges,
    formData,
    setFormData,
    selectedProducts,
    handleSave: originalHandleSave,
    handleCancel: originalHandleCancel,
    updateProductQuantity,
    currentTotalBottles,
    targetBottles,
    isBottleCountCorrect,
    subtotal,
    discountAmount,
    total
  } = useSubscriptionForm(id);
  // Get activities from shared context
  const {
    getActivitiesByCustomer,
    addActivity
  } = useActivities();
  // Link subscription to customer ID (in real app, this would come from subscription data)
  // For now, we'll use customer ID '1' for Sarah Johnson
  const customerId = '1'; // In real app: subscription?.customerId
  const subscriptionActivities = getActivitiesByCustomer(customerId);
  // Get tags from shared context
  const {
    allTags,
    getCustomerTags,
    addTagToCustomer,
    removeTagFromCustomer,
    createTag
  } = useTags();
  const subscriptionTags = getCustomerTags(customerId);
  // Calculate if customer contact info has changed
  const customerContactHasChanges = customerEmail !== initialCustomerEmail || customerPhone !== initialCustomerPhone;
  // Combined hasChanges that includes customer contact changes
  const hasChanges = formHasChanges || customerContactHasChanges;
  // Initialize customer contact info from subscription data
  useEffect(() => {
    if (subscription) {
      const email = subscription.customer;
      const phone = subscription.customerPhone || '+61 4XX XXX XXX';
      setCustomerEmail(email);
      setCustomerPhone(phone);
      setInitialCustomerEmail(email);
      setInitialCustomerPhone(phone);
    }
  }, [subscription]);
  // Tag handlers - use shared context
  const handleAddTag = (tag: TagData) => {
    addTagToCustomer(customerId, tag);
  };
  const handleRemoveTag = (tagId: string) => {
    removeTagFromCustomer(customerId, tagId);
  };
  // Create new tag handler
  const handleCreateTag = (label: string, color?: string) => {
    // Generate a unique ID for the new tag
    const newTagId = label.toLowerCase().replace(/\s+/g, '-');
    const newTag: TagData = {
      id: newTagId,
      label: label,
      color: (color || 'blue') as 'blue' | 'green' | 'orange' | 'purple' | 'gray'
    };
    createTag(newTag);
    addTagToCustomer(customerId, newTag);
  };
  // Handle adding a note to the timeline
  const handleAddNote = (noteText: string) => {
    addActivity(customerId, {
      type: 'note',
      date: new Date().toLocaleDateString('en-GB').split('/').join('-'),
      title: 'Admin Note',
      description: noteText,
      author: 'Sarah Admin'
    });
  };
  const handleAddTask = (task: TaskData) => {
    addActivity(customerId, {
      type: 'task',
      date: new Date().toLocaleDateString('en-GB').split('/').join('-'),
      title: task.title,
      description: `Task assigned to ${task.assignee} - Priority: ${task.priority}. Due: ${task.dueDate}${task.dueTime ? ' ' + task.dueTime : ''}`,
      author: 'Sarah Admin'
    });
  };
  // Enhanced save handler that includes customer contact info
  const handleSave = () => {
    // Save customer contact changes
    console.log('Saving customer email:', customerEmail);
    console.log('Saving customer phone:', customerPhone);
    // Update initial values after save
    setInitialCustomerEmail(customerEmail);
    setInitialCustomerPhone(customerPhone);
    // Call original save handler
    originalHandleSave();
  };
  // Enhanced cancel handler that reverts customer contact info
  const handleCancel = () => {
    // Revert customer contact changes
    setCustomerEmail(initialCustomerEmail);
    setCustomerPhone(initialCustomerPhone);
    // Call original cancel handler
    originalHandleCancel();
  };
  // Format date for display (e.g., "15 Jan 2024, 9:00 AM")
  const formatDateTimeDisplay = (dateTimeString: string): string => {
    if (!dateTimeString) return '';
    // Handle both "YYYY-MM-DD" and "YYYY-MM-DD HH:MM" formats
    const [datePart, timePart] = dateTimeString.split(' ');
    if (!datePart) return dateTimeString;
    const [year, month, day] = datePart.split('-').map(Number);
    // Validate date parts
    if (!year || !month || !day) return dateTimeString;
    const date = new Date(year, month - 1, day);
    // Check if date is valid
    if (isNaN(date.getTime())) return dateTimeString;
    const dateStr = date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    // If time part exists, format it
    if (timePart && timePart.includes(':')) {
      const [hours, minutes] = timePart.split(':').map(Number);
      // Validate time parts
      if (isNaN(hours) || isNaN(minutes)) return dateStr;
      const hour = hours % 12 || 12;
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const minuteStr = minutes.toString().padStart(2, '0');
      return `${dateStr}, ${hour}:${minuteStr} ${ampm}`;
    }
    // If no time part, just return the date
    return dateStr;
  };
  // Handle date/time change from picker
  const handleDateTimeChange = (newDateTime: string) => {
    handleFormChange('nextPayment', newDateTime);
  };
  // Payment retry handlers
  const handleRetryNow = async () => {
    console.log('Retrying payment now...');
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    // Show success toast (in real app)
    alert('Payment retry initiated successfully!');
  };
  const handleSendPaymentLink = async (includeMessage: boolean, message: string) => {
    console.log('Sending payment link...', {
      includeMessage,
      message
    });
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    // Show success toast (in real app)
    alert(`Payment link sent to ${customerEmail}`);
  };
  // Get retry history for this subscription
  const retryHistory = mockPaymentRetryHistory[subscription?.id || ''] || [];
  // Helper to format payment status badge
  const getPaymentStatusBadge = () => {
    const paymentInfo = subscription?.paymentInfo;
    if (!paymentInfo || paymentInfo.status === 'healthy') {
      return <span className="text-[13px] text-[#94A3B8]" data-id="element-9304">—</span>;
    }
    if (paymentInfo.status === 'in-retry') {
      const isFinalRetry = paymentInfo.retryCount === paymentInfo.maxRetries;
      return <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${isFinalRetry ? 'bg-[#F59E0B] text-white border-[#F59E0B]' : 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]'}`} data-id="element-9305">
          {isFinalRetry ? 'Final Retry' : `Retry ${paymentInfo.retryCount}/${paymentInfo.maxRetries}`}
        </span>;
    }
    if (paymentInfo.status === 'on-hold') {
      return <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-[#FEF9C3] text-[#854D0E] border border-[#FDE047]" data-id="element-9306">
          On Hold
        </span>;
    }
    if (paymentInfo.status === 'failed') {
      return <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-[#EF4444] text-white border border-[#EF4444]" data-id="element-9307">
          Failed
        </span>;
    }
    return null;
  };
  // Helper to format next retry date
  const formatNextRetry = (nextRetry?: string): string => {
    if (!nextRetry) return '—';
    const retryDate = new Date(nextRetry);
    const now = new Date();
    const diffMs = retryDate.getTime() - now.getTime();
    if (diffMs < 0) {
      return 'Overdue';
    }
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 24) {
      return `Tomorrow at ${retryDate.toLocaleTimeString('en-AU', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}`;
    }
    return retryDate.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short'
    }) + ' at ' + retryDate.toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  const handlePaymentUpdate = (paymentData: PaymentData) => {
    const firstDigit = paymentData.cardNumber.charAt(0);
    let cardType = 'Card';
    if (firstDigit === '4') cardType = 'Visa';else if (firstDigit === '5') cardType = 'Mastercard';else if (firstDigit === '3') cardType = 'Amex';
    const last4 = paymentData.cardNumber.replace(/\s/g, '').slice(-4);
    const expiry = `${paymentData.expiryMonth}/${paymentData.expiryYear}`;
    setPaymentMethod({
      type: cardType,
      last4,
      expiry
    });
  };
  const handleFormChange = (field: keyof typeof formData, value: string) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };
  const handleCopyShippingToBilling = (checked: boolean) => {
    setCopyShippingToBilling(checked);
    if (checked) {
      setFormData({
        ...formData,
        billingFirstName: formData.shippingFirstName,
        billingLastName: formData.shippingLastName,
        billingAddress1: formData.shippingAddress1,
        billingAddress2: formData.shippingAddress2,
        billingCity: formData.shippingCity,
        billingState: formData.shippingState,
        billingPostCode: formData.shippingPostCode,
        billingCountry: formData.shippingCountry
      });
    }
  };
  if (loading || !subscription) {
    return <div className="flex h-screen bg-[#F1F5F9] items-center justify-center" data-id="element-9308">
        <div className="text-[#475569]" data-id="element-9309">Loading subscription details...</div>
      </div>;
  }
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-9310">
      <SubscriptionHeader subscription={subscription} isEditing={isEditing} onEdit={() => setIsEditing(true)} onSave={handleSave} onCancel={handleCancel} onBack={() => navigate('/subscriptions')} onViewContact={() => navigate('/contacts/1')} isSaveDisabled={false} hasChanges={hasChanges} data-id="element-9311" />

      {/* Payment Status Banner */}
      {subscription.paymentInfo && subscription.paymentInfo.status !== 'healthy' && <PaymentStatusBanner paymentInfo={subscription.paymentInfo} customerEmail={customerEmail} onRetryNow={handleRetryNow} onSendPaymentLink={() => setIsSendPaymentLinkModalOpen(true)} data-id="element-9312" />}

      <div className="flex-1 overflow-auto" data-id="element-9313">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-9314">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6" data-id="element-9315">
            {/* Subscription Details Card */}
            <Card padding="none" data-id="element-9316">
              <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-9317">
                <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-9318">
                  Subscription Details
                </h3>
              </div>

              <div className="p-6 bg-white" data-id="element-9319">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-9320">
                  <div data-id="element-9321">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-9322">
                      Created on
                    </label>
                    <div className="text-[14px] text-[#0F172A] font-medium" data-id="element-9323">
                      {subscription.startDate}
                    </div>
                  </div>

                  <div data-id="element-9324">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-9325">
                      Next payment due
                    </label>
                    {isEditing ? <div className="relative" data-id="element-9326">
                        <button onClick={() => setIsDatePickerOpen(true)} className="w-full h-10 px-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[#0F172A] text-[14px] text-left hover:border-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all flex items-center justify-between" data-id="element-9327">
                          <span className="font-medium" data-id="element-9328">
                            {formatDateTimeDisplay(formData.nextPayment)}
                          </span>
                          <Calendar className="w-4 h-4 text-[#64748B]" data-id="element-9329" />
                        </button>
                      </div> : <div className="text-[14px] text-[#0F172A] font-medium" data-id="element-9330">
                        {formatDateTimeDisplay(subscription.nextPayment)}
                      </div>}
                  </div>

                  <div data-id="element-9331">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-9332">
                      Status
                    </label>
                    {isEditing ? <select className="w-full h-10 px-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[#0F172A] text-[14px] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" value={formData.status} onChange={e => handleFormChange('status', e.target.value)} data-id="element-9333">
                        <option value="Active" data-id="element-9334">Active</option>
                        <option value="Paused" data-id="element-9335">Paused</option>
                        <option value="Cancelled" data-id="element-9336">Cancelled</option>
                        <option value="On Hold" data-id="element-9337">On Hold (tag applied)</option>
                      </select> : <div className="inline-flex" data-id="element-9338">
                        <span className={`px-3 py-1.5 rounded-md text-[12px] font-medium ${subscription.status === 'Active' ? 'bg-[#D1FAE5] text-[#059669] border border-[#BBF7D0]' : subscription.status === 'Paused' ? 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE]' : subscription.status === 'Cancelled' ? 'bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0]' : 'bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A]'}`} data-id="element-9339">
                          {subscription.status}
                        </span>
                      </div>}
                  </div>

                  {isEditing && formData.status === 'Paused' && <div className="md:col-span-3" data-id="element-9340">
                      <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-9341">
                        Pause Duration
                      </label>
                      <select className="w-full md:w-1/3 h-10 px-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[#0F172A] text-[14px] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" value={formData.pauseDuration} onChange={e => handleFormChange('pauseDuration', e.target.value)} data-id="element-9342">
                        <option value="" data-id="element-9343">Select Duration</option>
                        <option value="1 Cycle" data-id="element-9344">1 Cycle</option>
                        <option value="2 Cycles" data-id="element-9345">2 Cycles</option>
                        <option value="3 Cycles" data-id="element-9346">3 Cycles</option>
                        <option value="Indefinite" data-id="element-9347">Until Reactivated</option>
                      </select>
                    </div>}
                </div>
              </div>

              {/* Order Now Button */}
              <div className="px-6 pb-6 bg-white" data-id="element-9348">
                <button onClick={() => setIsOrderNowModalOpen(true)} className="inline-flex items-center gap-2 h-10 px-5 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-md hover:bg-[#0284C7] active:bg-[#0369A1] focus:outline-none focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200 ease-in-out" data-id="element-9349">
                  <ShoppingCart className="w-[18px] h-[18px]" data-id="element-9350" />
                  Order Now
                </button>
              </div>
            </Card>

            {/* Items / Wine Bottles Section */}
            <ProductSelector isEditing={isEditing} formData={formData} setFormData={setFormData} selectedProducts={selectedProducts} updateProductQuantity={updateProductQuantity} availableProducts={availableProducts} currentTotalBottles={currentTotalBottles} targetBottles={targetBottles} isBottleCountCorrect={isBottleCountCorrect} subscriptionItems={subscription.items} subtotal={subtotal} discountPercent={subscription.discountPercent} discountAmount={discountAmount} total={total} subscriptionTitle={subscription.subscriptionTitle} data-id="element-9351" />

            {/* Orders Table - NEW COMPONENT */}
            <OrdersTable upcomingOrders={[{
            id: 'upcoming-1',
            date: subscription.nextPayment,
            orderId: '#4012',
            chargeId: '#1210458823',
            amount: '$126.50',
            status: 'pending',
            items: [{
              name: '2025 Fiano',
              quantity: 2,
              price: '$35.00'
            }, {
              name: '2024 Shiraz',
              quantity: 2,
              price: '$45.00'
            }],
            subtotal: '$160.00',
            taxes: '$16.00',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$176.00',
            chargeCreatedAt: subscription.nextPayment,
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }, {
            id: 'upcoming-2',
            date: '2024-02-15',
            orderId: '#4013',
            chargeId: '#1210458824',
            amount: '$142.00',
            status: 'pending',
            items: [{
              name: '2024 Cabernet Sauvignon',
              quantity: 3,
              price: '$48.00'
            }, {
              name: '2025 Rosé',
              quantity: 3,
              price: '$32.00'
            }],
            subtotal: '$240.00',
            taxes: '$24.00',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$264.00',
            chargeCreatedAt: '2024-02-15',
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }, {
            id: 'upcoming-3',
            date: '2024-03-15',
            orderId: '#4014',
            chargeId: '#1210458825',
            amount: '$98.50',
            status: 'pending',
            items: [{
              name: '2025 Sauvignon Blanc',
              quantity: 2,
              price: '$38.00'
            }, {
              name: '2024 Merlot',
              quantity: 2,
              price: '$42.00'
            }],
            subtotal: '$160.00',
            taxes: '$16.00',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$176.00',
            chargeCreatedAt: '2024-03-15',
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }, {
            id: 'upcoming-4',
            date: '2024-04-15',
            orderId: '#4015',
            chargeId: '#1210458826',
            amount: '$156.00',
            status: 'pending',
            items: [{
              name: '2024 Pinot Noir',
              quantity: 4,
              price: '$52.00'
            }, {
              name: '2025 Chardonnay',
              quantity: 2,
              price: '$40.00'
            }],
            subtotal: '$288.00',
            taxes: '$28.80',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$316.80',
            chargeCreatedAt: '2024-04-15',
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }]} recentOrders={[{
            id: '1',
            date: '2023-12-15',
            orderId: '#3873',
            chargeId: '#1180247974',
            amount: '$126.50',
            status: 'success',
            isCheckout: true,
            items: [{
              name: '2025 Fiano',
              quantity: 2,
              price: '$35.00'
            }],
            subtotal: '$70.00',
            taxes: '$7.00',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$77.00',
            chargeCreatedAt: '2023-12-15',
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }, {
            id: '2',
            date: '2023-11-15',
            orderId: '#3872',
            chargeId: '#1180247973',
            amount: '$142.00',
            status: 'success',
            items: [{
              name: '2024 Shiraz',
              quantity: 3,
              price: '$45.00'
            }, {
              name: '2024 Grenache',
              quantity: 3,
              price: '$38.00'
            }],
            subtotal: '$249.00',
            taxes: '$24.90',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$273.90',
            chargeCreatedAt: '2023-11-15',
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }, {
            id: '3',
            date: '2023-10-15',
            orderId: '#3871',
            chargeId: '#1180247972',
            amount: '$98.50',
            status: 'success',
            items: [{
              name: '2024 Riesling',
              quantity: 2,
              price: '$36.00'
            }, {
              name: '2024 Tempranillo',
              quantity: 2,
              price: '$44.00'
            }],
            subtotal: '$160.00',
            taxes: '$16.00',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$176.00',
            chargeCreatedAt: '2023-10-15',
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }, {
            id: '4',
            date: '2023-09-15',
            orderId: '#3870',
            chargeId: '#1180247971',
            amount: '$134.00',
            status: 'success',
            items: [{
              name: '2024 Cabernet Sauvignon',
              quantity: 2,
              price: '$48.00'
            }, {
              name: '2024 Pinot Grigio',
              quantity: 2,
              price: '$35.00'
            }, {
              name: '2024 Syrah',
              quantity: 2,
              price: '$46.00'
            }],
            subtotal: '$258.00',
            taxes: '$25.80',
            shipping: '$0.00',
            shippingType: 'Standard',
            total: '$283.80',
            chargeCreatedAt: '2023-09-15',
            paymentProcessor: 'Shopify Payment Gateway',
            shippingAddress: {
              name: `${formData.shippingFirstName} ${formData.shippingLastName}`,
              street: formData.shippingAddress1,
              street2: formData.shippingAddress2,
              city: formData.shippingCity,
              state: formData.shippingState,
              zip: formData.shippingPostCode,
              country: formData.shippingCountry
            }
          }]} data-id="element-9352" />

            {/* Payment Retry History - Only show if there's retry history */}
            {retryHistory.length > 0 && <PaymentRetryHistory retryAttempts={retryHistory} data-id="element-9353" />}

            {/* Activity Timeline */}
            <ActivityTimeline events={subscriptionActivities} onAddNote={handleAddNote} onAddTask={() => setIsCreateTaskModalOpen(true)} data-id="element-9354" />
          </div>

          {/* Right Column - Customer Summary & Addresses */}
          <div className="space-y-6" data-id="element-9355">
            <CustomerSummary customerName={subscription.customerName} customerEmail={customerEmail} customerPhone={customerPhone} totalPaid="$1,240.00" totalSaved="$124.00" isEditing={isEditing} onEmailChange={setCustomerEmail} onPhoneChange={setCustomerPhone} data-id="element-9356" />

            {/* View Contact Button */}
            <div className="px-6 -mt-3" data-id="element-9357"></div>

            {/* Payment Status Card - Only show if not healthy */}
            {subscription.paymentInfo && subscription.paymentInfo.status !== 'healthy' && <Card padding="none" data-id="element-9358">
                  <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-9359">
                    <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-9360">
                      Payment Status
                    </h3>
                  </div>
                  <div className="p-6 bg-white space-y-4" data-id="element-9361">
                    <div data-id="element-9362">
                      <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-9363">
                        Status
                      </label>
                      <div data-id="element-9364">{getPaymentStatusBadge()}</div>
                    </div>

                    {subscription.paymentInfo.nextRetry && <div data-id="element-9365">
                        <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-9366">
                          Next Retry
                        </label>
                        <div className="text-[14px] text-[#0F172A]" data-id="element-9367">
                          {formatNextRetry(subscription.paymentInfo.nextRetry)}
                        </div>
                      </div>}
                  </div>
                </Card>}

            {/* Payment Method Card */}
            <Card padding="none" data-id="element-9368">
              <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-9369">
                <div className="flex items-center justify-between" data-id="element-9370">
                  <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-9371">
                    Payment Method
                  </h3>
                  <button onClick={() => setIsPaymentModalOpen(true)} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-9372">
                    Update
                  </button>
                </div>
              </div>

              <div className="p-6 bg-white" data-id="element-9373">
                <div className="flex items-center gap-3 bg-[#F8FAFC] p-3 rounded-md border border-[#E2E8F0]" data-id="element-9374">
                  <CreditCard className="w-5 h-5 text-[#475569]" data-id="element-9375" />
                  <div className="flex-1" data-id="element-9376">
                    <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-9377">
                      {paymentMethod.type} ending in {paymentMethod.last4}
                    </div>
                    <div className="flex items-center gap-2 mt-1" data-id="element-9378">
                      <span className="text-[12px] text-[#94A3B8]" data-id="element-9379">
                        Expires {paymentMethod.expiry}
                      </span>
                      {isCardExpired(paymentMethod.expiry) && <span className="inline-flex items-center px-2 py-0.5 rounded text-[12px] font-medium bg-[#FEE2E2] text-[#DC2626] border border-[#FECACA]" data-id="element-9380">
                          Expired
                        </span>}
                      {!isCardExpired(paymentMethod.expiry) && isCardExpiringSoon(paymentMethod.expiry) && <span className="inline-flex items-center px-2 py-0.5 rounded text-[12px] font-medium bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A]" data-id="element-9381">
                            Expiring Soon
                          </span>}
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Tags Card */}
            <Card padding="none" data-id="element-9382">
              <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-9383">
                <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-9384">
                  Tags
                </h3>
              </div>
              <div className="p-6 bg-white" data-id="element-9385">
                <TagInput tags={subscriptionTags} availableTags={allTags} onAddTag={handleAddTag} onRemoveTag={handleRemoveTag} onCreateTag={handleCreateTag} placeholder="Add or create tag..." data-id="element-9386" />
                <p className="text-[12px] text-[#94A3B8] mt-2" data-id="element-9387">
                  Type a tag name and press Enter to create a new tag
                </p>
              </div>
            </Card>

            {/* Addresses - Compact view or editable forms */}
            {isEditing ? <>
                {/* Shipping Address Form */}
                <AddressForm type="shipping" formData={formData} onChange={(field, value) => handleFormChange(field, value)} disabled={false} data-id="element-9388" />

                {/* Delivery Instructions */}
                <Card padding="none" className="-mt-3" data-id="element-9389">
                  <div className="p-6 bg-white" data-id="element-9390">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-9391">
                      Delivery Instructions
                    </label>
                    <textarea value={formData.deliveryInstructions} onChange={e => handleFormChange('deliveryInstructions', e.target.value)} placeholder="e.g., Leave at front door, Call on arrival, etc." rows={3} className="w-full px-3 py-2.5 border border-[#CBD5E1] rounded-md bg-white text-[#0F172A] text-[14px] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-[border-color,box-shadow] duration-200 ease-in-out resize-none" data-id="element-9392" />
                  </div>
                </Card>

                {/* Copy to Billing Checkbox */}
                <div className="flex items-center gap-2 px-6 -mt-3" data-id="element-9393">
                  <input type="checkbox" id="copyToBilling" checked={copyShippingToBilling} onChange={e => handleCopyShippingToBilling(e.target.checked)} className="w-[18px] h-[18px] text-[#0EA5E9] border-2 border-[#CBD5E1] rounded focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20" data-id="element-9394" />
                  <label htmlFor="copyToBilling" className="text-[14px] text-[#0F172A] cursor-pointer select-none" data-id="element-9395">
                    Billing address same as shipping
                  </label>
                </div>

                {/* Billing Address Form */}
                <AddressForm type="billing" formData={formData} onChange={(field, value) => handleFormChange(field, value)} disabled={false} data-id="element-9396" />
              </> : <>
                {/* Compact Shipping Address View */}
                <Card padding="none" data-id="element-9397">
                  <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-9398">
                    <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-9399">
                      Shipping Address
                    </h3>
                  </div>
                  <div className="p-6 bg-white" data-id="element-9400">
                    <div className="text-[14px] text-[#475569] leading-relaxed" data-id="element-9401">
                      <div className="font-medium text-[#0F172A]" data-id="element-9402">
                        {formData.shippingFirstName} {formData.shippingLastName}
                      </div>
                      <div data-id="element-9403">{formData.shippingAddress1}</div>
                      {formData.shippingAddress2 && <div data-id="element-9404">{formData.shippingAddress2}</div>}
                      <div data-id="element-9405">
                        {formData.shippingCity}, {formData.shippingState}{' '}
                        {formData.shippingPostCode}
                      </div>
                      <div data-id="element-9406">{formData.shippingCountry}</div>
                    </div>

                    {/* Delivery Instructions */}
                    <div className="mt-4 pt-4 border-t border-[#E2E8F0]" data-id="element-9407">
                      <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-9408">
                        Delivery Instructions
                      </label>
                      {formData.deliveryInstructions ? <div className="text-[14px] text-[#475569] bg-[#F8FAFC] p-3 rounded-md border border-[#E2E8F0]" data-id="element-9409">
                          {formData.deliveryInstructions}
                        </div> : <div className="text-[14px] text-[#94A3B8] italic" data-id="element-9410">
                          No delivery instructions provided
                        </div>}
                    </div>
                  </div>
                </Card>

                {/* Compact Billing Address View */}
                <Card padding="none" data-id="element-9411">
                  <div className="p-6 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-9412">
                    <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-9413">
                      Billing Address
                    </h3>
                  </div>
                  <div className="p-6 bg-white" data-id="element-9414">
                    <div className="text-[14px] text-[#475569] leading-relaxed" data-id="element-9415">
                      <div className="font-medium text-[#0F172A]" data-id="element-9416">
                        {formData.billingFirstName} {formData.billingLastName}
                      </div>
                      <div data-id="element-9417">{formData.billingAddress1}</div>
                      {formData.billingAddress2 && <div data-id="element-9418">{formData.billingAddress2}</div>}
                      <div data-id="element-9419">
                        {formData.billingCity}, {formData.billingState}{' '}
                        {formData.billingPostCode}
                      </div>
                      <div data-id="element-9420">{formData.billingCountry}</div>
                    </div>
                  </div>
                </Card>
              </>}
          </div>
        </div>
      </div>

      {/* Payment Method Modal */}
      <PaymentMethodModal isOpen={isPaymentModalOpen} onClose={() => setIsPaymentModalOpen(false)} onSave={handlePaymentUpdate} currentCard={paymentMethod} data-id="element-9421" />

      {/* DateTime Picker Modal */}
      {isDatePickerOpen && <DateTimePicker value={formData.nextPayment} onChange={handleDateTimeChange} onClose={() => setIsDatePickerOpen(false)} data-id="element-9422" />}

      {/* Send Payment Link Modal */}
      <SendPaymentLinkModal isOpen={isSendPaymentLinkModalOpen} onClose={() => setIsSendPaymentLinkModalOpen(false)} onSend={handleSendPaymentLink} customerEmail={customerEmail} data-id="element-9423" />

      {/* Create Task Modal */}
      <CreateTaskModal isOpen={isCreateTaskModalOpen} onClose={() => setIsCreateTaskModalOpen(false)} onCreate={handleAddTask} initialLinkedTo={{
      id: subscription.id,
      type: 'subscription',
      label: subscription.subscriptionTitle
    }} data-id="element-9424" />

      {/* Order Now Confirmation Modal */}
      {isOrderNowModalOpen && <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsOrderNowModalOpen(false)} data-id="element-9425" />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-9426">
            <div className="bg-white rounded-lg shadow-xl w-[520px] max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-9427">
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-9428">
                <div className="flex items-center gap-3" data-id="element-9429">
                  <div className="w-10 h-10 rounded-md bg-[#E0F2FE] flex items-center justify-center" data-id="element-9430">
                    <ShoppingCart className="w-5 h-5 text-[#0EA5E9]" data-id="element-9431" />
                  </div>
                  <div data-id="element-9432">
                    <h2 className="text-[18px] font-semibold text-[#0F172A]" data-id="element-9433">
                      Create Order Now
                    </h2>
                    <p className="text-[13px] text-[#64748B]" data-id="element-9434">
                      Confirm order for {subscription.customerName}
                    </p>
                  </div>
                </div>
                <button onClick={() => setIsOrderNowModalOpen(false)} className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-9435">
                  <X className="w-5 h-5 text-[#64748B]" data-id="element-9436" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-5 overflow-y-auto space-y-5" data-id="element-9437">
                {/* Info Banner */}
                <div className="bg-[#E0F2FE] border border-[#7DD3FC] rounded-md px-4 py-3 flex items-start gap-2.5" data-id="element-9438">
                  <AlertTriangle className="w-4 h-4 text-[#0369A1] mt-0.5 shrink-0" data-id="element-9439" />
                  <p className="text-[13px] text-[#0369A1]" data-id="element-9440">
                    This will immediately create and process an order using the
                    current selection below. The next payment date will reset
                    based on the billing cycle (
                    {formData.frequency.replace('Every ', '').toLowerCase()}{' '}
                    from today).
                  </p>
                </div>

                {/* Current Selection Table */}
                <div data-id="element-9441">
                  <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-[0.5px] mb-3" data-id="element-9442">
                    Current Selection
                  </h3>
                  <div className="border border-[#E2E8F0] rounded-md overflow-hidden" data-id="element-9443">
                    <table className="w-full" data-id="element-9444">
                      <thead className="bg-[#F1F5F9]" data-id="element-9445">
                        <tr data-id="element-9446">
                          <th className="px-4 py-2.5 text-left text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-9447">
                            Item
                          </th>
                          <th className="px-4 py-2.5 text-center text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-9448">
                            Qty
                          </th>
                          <th className="px-4 py-2.5 text-right text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-9449">
                            Price
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E2E8F0]" data-id="element-9450">
                        {subscription.items?.map((item: any, index: number) => {
                      const product = availableProducts.find(p => p.name === item.name);
                      const unitPrice = product?.price || 0;
                      return <tr key={index} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-9451">
                              <td className="px-4 py-3" data-id="element-9452">
                                <div className="flex items-center gap-2" data-id="element-9453">
                                  <Package className="w-4 h-4 text-[#94A3B8]" data-id="element-9454" />
                                  <span className="text-[14px] text-[#0F172A]" data-id="element-9455">
                                    {item.name}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center" data-id="element-9456">
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-[#F1F5F9] text-[13px] font-medium text-[#0F172A]" data-id="element-9457">
                                  {item.quantity}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right text-[14px] text-[#0F172A] font-medium" data-id="element-9458">
                                ${(unitPrice * item.quantity).toFixed(2)}
                              </td>
                            </tr>;
                    })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pricing Summary */}
                <div className="bg-[#F8FAFC] rounded-md border border-[#E2E8F0] p-4 space-y-2" data-id="element-9459">
                  <div className="flex items-center justify-between" data-id="element-9460">
                    <span className="text-[14px] text-[#475569]" data-id="element-9461">Subtotal</span>
                    <span className="text-[14px] text-[#0F172A] font-medium" data-id="element-9462">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  {discountAmount > 0 && <div className="flex items-center justify-between" data-id="element-9463">
                      <span className="text-[14px] text-[#10B981]" data-id="element-9464">
                        Discount ({subscription.discountPercent}%)
                      </span>
                      <span className="text-[14px] text-[#10B981] font-medium" data-id="element-9465">
                        -${discountAmount.toFixed(2)}
                      </span>
                    </div>}
                  <div className="flex items-center justify-between pt-2 border-t border-[#E2E8F0]" data-id="element-9466">
                    <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-9467">
                      Total
                    </span>
                    <span className="text-[16px] font-bold text-[#0EA5E9]" data-id="element-9468">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Shipping Address */}
                <div data-id="element-9469">
                  <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-[0.5px] mb-2" data-id="element-9470">
                    Shipping To
                  </h3>
                  <div className="text-[14px] text-[#475569] leading-relaxed bg-[#F8FAFC] rounded-md border border-[#E2E8F0] p-3" data-id="element-9471">
                    <div className="font-medium text-[#0F172A]" data-id="element-9472">
                      {formData.shippingFirstName} {formData.shippingLastName}
                    </div>
                    <div data-id="element-9473">{formData.shippingAddress1}</div>
                    {formData.shippingAddress2 && <div data-id="element-9474">{formData.shippingAddress2}</div>}
                    <div data-id="element-9475">
                      {formData.shippingCity}, {formData.shippingState}{' '}
                      {formData.shippingPostCode}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-end gap-3 bg-white" data-id="element-9476">
                <button onClick={() => setIsOrderNowModalOpen(false)} className="h-10 px-5 text-[14px] font-medium text-[#0F172A] bg-white border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] hover:border-[#94A3B8] transition-all duration-200" data-id="element-9477">
                  Cancel
                </button>
                <button onClick={() => {
              console.log('Order created!', {
                items: subscription.items,
                total,
                shippingAddress: {
                  firstName: formData.shippingFirstName,
                  lastName: formData.shippingLastName,
                  address1: formData.shippingAddress1,
                  city: formData.shippingCity,
                  state: formData.shippingState,
                  postCode: formData.shippingPostCode
                }
              });
              setIsOrderNowModalOpen(false);
              alert('Order created successfully!');
            }} className="inline-flex items-center gap-2 h-10 px-5 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-md hover:bg-[#0284C7] active:bg-[#0369A1] focus:outline-none focus:ring-[3px] focus:ring-[#0EA5E9]/20 transition-all duration-200" data-id="element-9478">
                  <ShoppingCart className="w-4 h-4" data-id="element-9479" />
                  Confirm Order
                </button>
              </div>
            </div>
          </div>
        </>}
    </div>;
}